import _isEqual from 'lodash/isEqual';
import moment from 'moment';

import { getAgencyTime } from './CalendarUtil';
import { getCustomerFilterValues, getDateFilterValues } from './CSLPFilterUtil';

export const isValidDateFilter = (value: DateFilterType | string) =>
  value === DateFilterType.ANYTIME ||
  value === DateFilterType.OVERDUE ||
  value === DateFilterType.NEXT_SEVEN_DAYS ||
  value === DateFilterType.LAST_SEVEN_DAYS ||
  value === DateFilterType.LAST_THIRTY_DAYS ||
  value === DateFilterType.TODAY ||
  value === DateFilterType.YESTERDAY;

export const isValidNumberFilter = (value: CaseNumberFilterType | string) =>
  value === CaseNumberFilterType.ANY ||
  value === CaseNumberFilterType.OPTION_ONE ||
  value === CaseNumberFilterType.OPTION_TWO ||
  value === CaseNumberFilterType.OPTION_THREE;

export const getDateFilterValue = (filterLabel: DateFilterType | string, inclusivity: 'start' | 'end') => {
  const currentAgencyDate = getAgencyTime();
  switch (filterLabel) {
    case DateFilterType.ANYTIME:
      return '';

    case DateFilterType.TODAY:
      return (inclusivity === 'start' ? currentAgencyDate.startOf('days') : currentAgencyDate.endOf('days'))
        .utc()
        .format();

    case DateFilterType.LAST_SEVEN_DAYS:
      return (
        inclusivity === 'start'
          ? currentAgencyDate.subtract(7, 'days').startOf('days')
          : currentAgencyDate.subtract(1, 'days').endOf('days')
      )
        .utc()
        .format();

    case DateFilterType.LAST_THIRTY_DAYS:
      return (
        inclusivity === 'start'
          ? currentAgencyDate.subtract(30, 'days').startOf('days')
          : currentAgencyDate.subtract(1, 'days').endOf('days')
      )
        .utc()
        .format();

    case DateFilterType.OVERDUE:
      return inclusivity === 'start' ? '' : currentAgencyDate.subtract(1, 'days').endOf('days').utc().format();

    case DateFilterType.NEXT_SEVEN_DAYS:
      return (
        inclusivity === 'start'
          ? currentAgencyDate.add(1, 'days').startOf('days')
          : currentAgencyDate.add(7, 'days').endOf('days')
      )
        .utc()
        .format();

    default:
      if (!filterLabel) {
        return '';
      }

      const dateRange = filterLabel.split('-');
      const start = dateRange[0];
      const end = dateRange[1];
      const now = getAgencyTime();
      const time = {
        hour:
          (inclusivity === 'start' ? 0 : 23) +
          Math.floor(now.utcOffset() / -60) +
          Math.floor(new Date().getTimezoneOffset() / -60),
        minute:
          (inclusivity === 'start' ? 0 : 59) + (now.utcOffset() % 60) * -1 + (new Date().getTimezoneOffset() % 60) * -1,
        second: inclusivity === 'start' ? 0 : 59,
      };

      return inclusivity === 'start'
        ? moment(start, 'MM/DD/YYYY').set(time).utc().format()
        : moment(end, 'MM/DD/YYYY').set(time).utc().format();
  }
};

export const getNumberFilterValue = (filterLabel: CaseNumberFilterType | string, inclusivity: 'max' | 'min') => {
  switch (filterLabel) {
    case CaseNumberFilterType.ANY:
      return null;

    case CaseNumberFilterType.OPTION_ONE:
      return inclusivity === 'max' ? 0 : 10;

    case CaseNumberFilterType.OPTION_TWO:
      return inclusivity === 'max' ? 10 : 20;

    case CaseNumberFilterType.OPTION_THREE:
      return inclusivity === 'max' ? 20 : null;

    default:
      if (!filterLabel) {
        return null;
      }
      const range = filterLabel.split('-');

      return inclusivity === 'max' ? parseFloat(range[0]) : parseFloat(range[1]);
  }
};

export const getCurrencyFilterValue = (filterLabel: CurrencyFilterType | string, inclusivity: 'max' | 'min') => {
  switch (filterLabel) {
    case CurrencyFilterType.ANY:
    case CurrencyFilterType.OPTION_THREE:
      return null;

    case CurrencyFilterType.OPTION_ONE:
      return inclusivity === 'min' ? 0 : 100;

    case CurrencyFilterType.OPTION_TWO:
      return inclusivity === 'min' ? 100 : null;

    default:
      if (!filterLabel) {
        return null;
      }
      const range = filterLabel.split('-');

      return inclusivity === 'min' ? parseFloat(range[0]) : parseFloat(range[1]);
  }
};

export const getAssigneeFilterValues = (assignees: ICaseUserResponse[], selectedIds: number[]) => {
  if (assignees && assignees.length) {
    const caseAssignees = assignees
      .filter((c) => selectedIds.includes(c.id))
      .map((c) => `${c.firstName} ${c.lastName}`);

    return caseAssignees.join(', ');
  }

  return '-';
};

export const getForcedAbatementActivitiesValues = (
  forcedAbatementActivities: IConfigForcedAbatementResponse[],
  selectedIds: number[]
) => {
  if (forcedAbatementActivities && forcedAbatementActivities.length) {
    const activities = forcedAbatementActivities.filter((c) => selectedIds.includes(c.id)).map((c) => `${c.label}`);

    return activities.join(', ');
  }

  return '-';
};

export const getViolationsValues = (violationList: IXerceConfigViolationListResponse[], selectedIds: number[]) => {
  if (violationList && violationList.length) {
    const violations = violationList.filter((c) => selectedIds.includes(c.id)).map((c) => `${c.label}`);

    return violations.join(', ');
  }

  return '-';
};

export const getViolationTypesValues = (violationTypes: IConfigXerceViolationTypeResponse[], selectedIds: number[]) => {
  if (violationTypes && violationTypes.length) {
    const violations = violationTypes.filter((c) => selectedIds.includes(c.id)).map((c) => `${c.label}`);

    return violations.join(', ');
  }

  return '-';
};

export const getUpdatedFilterValues = (
  selectedfilters: ICaseFilters,
  defaultFilters: ICaseFilters,
  forcedAbatementActivities: IConfigForcedAbatementResponse[],
  violationList: IXerceConfigViolationListResponse[],
  configViolationTypes: IConfigXerceViolationTypeResponse[],
  assignees: ICaseUserResponse[],
  contacts: IContactsListResponse,
  externalSource: any
): IKeyValuePair => {
  const CSLPFiltersLabel = {
    caseAssignees: 'Case Assignee',
    inspectionAssignee: 'Inspection Assignee',
    closedAt: 'Closed At',
    createdAt: 'Created At',
    lastCompletedInspection: 'Last Completed Inspection',
    nextScheduledInspection: 'Next Scheduled Inspection',
    violations: 'Violations',
    violationTypes: 'Violation Types',
    status: 'Status',
    outstandingBalance: 'Outstanding Balance',
    initiationType: 'Initiation Type',
    hoursLogged: 'Hours Logged',
    forcedAbatementActivities: 'Forced Abatement Activities',
    contacts: 'Contacts',
    externalSource: 'Sources',
  };

  const filterValues = {};

  for (const key of Object.keys(selectedfilters)) {
    if (!_isEqual(defaultFilters[key], selectedfilters[key])) {
      let value;
      switch (key) {
        case 'forcedAbatementActivities':
          value = getForcedAbatementActivitiesValues(forcedAbatementActivities, selectedfilters[key]);
          break;
        case 'violations':
          value = getViolationsValues(violationList, selectedfilters[key]);
          break;
        case 'violationTypes':
          value = getViolationTypesValues(configViolationTypes, selectedfilters[key]);
          break;
        case 'contacts':
          value = getCustomerFilterValues(contacts);
          break;
        case 'caseAssignees':
        case 'inspectionAssignee':
          value = getAssigneeFilterValues(assignees, selectedfilters[key]);
          break;
        case 'closedAt':
        case 'createdAt':
        case 'lastCompletedInspection':
        case 'nextScheduledInspection':
          value = getDateFilterValues(selectedfilters[key]);
          break;
        case 'externalSource':
        case 'initiationType':
          value = selectedfilters[key].join(', ');
          break;
        case 'outstandingBalance':
          value = `${getCurrencyFilterValue(selectedfilters[key], 'min')}$ - ${getCurrencyFilterValue(
            selectedfilters[key],
            'max'
          )}$`;
          break;
        case 'hoursLogged':
          value = `${getNumberFilterValue(selectedfilters[key], 'min')} - ${getNumberFilterValue(
            selectedfilters[key],
            'max'
          )} hours`;
          break;
        default:
          value = JSON.stringify(selectedfilters[key]);
      }

      const label = CSLPFiltersLabel[key];
      filterValues[label] = value;
    }
  }

  return filterValues;
};
